import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { WaitlistEntry, WaitlistPriority } from '@vagusx/clients';
import { WAITLIST_PRIORITIES } from '@vagusx/clients';
import {
  appointmentToAutoPick,
  entryToFormValues,
  formFieldErrors,
  waitlistFormSchema,
  type WaitlistFormValues,
} from './waitlist-payload';
import { zodResolver } from '@/lib/zod-resolver';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAppointmentTypes } from '@/hooks/use-appointment-types';
import { EntityCombobox } from '@/components/custom/entity-combobox';
import { locationPickerConfig, providerPickerConfig } from '@/components/custom/entity-pickers';
import { PatientCombobox } from '@/components/custom/patient-combobox';
import { Input } from '@/components/ui/input';
import { SaveButton } from '@/components/custom/save-button';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DAY_PICKER_ORDER, DAY_SHORT, PRIORITY_LABEL } from './waitlist-format';

/** How many of the patient's upcoming appointments the picker offers. */
const UPCOMING_LIMIT = 20;

/** Select can't hold null: this stands for "none" / "any". */
const NONE = '__none__';

interface WaitlistEntryFormProps {
  /** The entry being edited; null to add one (then a patient is chosen here). */
  entry: WaitlistEntry | null;
  submitLabel: string;
  /**
   * Save the entry; throw what the API refused. A field error (e.g. the EHR
   * needs a location, ADR-292) is shown under its field, anything else toasts.
   */
  onSubmit: (values: WaitlistFormValues) => Promise<void>;
  onCancel: () => void;
}

/**
 * Fills the sheet below its header: the fields scroll, the actions stay
 * pinned to the bottom. The sheet's content must be a column (`flex flex-col`).
 */

export function WaitlistEntryForm({
  entry,
  submitLabel,
  onSubmit,
  onCancel,
}: WaitlistEntryFormProps): React.ReactElement {
  const form = useForm<WaitlistFormValues>({
    resolver: zodResolver(waitlistFormSchema),
    defaultValues: entryToFormValues(entry),
  });
  const { control, handleSubmit, formState, setValue, getValues, setError, clearErrors } = form;

  const submit = async (values: WaitlistFormValues): Promise<void> => {
    try {
      await onSubmit(values);
    } catch (err) {
      const fields = formFieldErrors(err);
      for (const { field, message } of fields) setError(field, { message });
      if (fields.length === 0) {
        toast.error(err instanceof Error ? err.message : 'An unexpected error occurred');
      }
    }
  };
  const patientId = useWatch({ control, name: 'patientId' });
  const appointmentId = useWatch({ control, name: 'appointmentId' });

  const { data: appointmentTypes = [] } = useAppointmentTypes();
  const { data: upcoming } = useQuery({
    queryKey: queryKeys.appointments.upcoming(patientId, UPCOMING_LIMIT),
    queryFn: () => getAppointmentClient().getUpcoming(patientId, UPCOMING_LIMIT),
    enabled: !!patientId,
  });
  const appointments = useMemo(() => upcoming?.appointments ?? [], [upcoming]);

  // Pick a new entry's appointment (`appointmentToAutoPick`) once per patient
  // chosen, so switching back to None sticks; an entry being edited keeps what
  // it has.
  const autoPickedFor = useRef<string | null>(null);
  useEffect(() => {
    if (entry || !patientId || !upcoming || autoPickedFor.current === patientId) return;
    autoPickedFor.current = patientId;
    const only = appointmentToAutoPick(upcoming.appointments, !!getValues('appointmentTypeId'));
    if (only) {
      setValue('appointmentId', only.id);
      setValue('appointmentTypeId', only.appointmentTypeId);
    }
  }, [entry, patientId, upcoming, setValue, getValues]);

  const linked = appointments.find((a) => a.id === appointmentId);
  const typeName = (id: string | null): string | undefined =>
    appointmentTypes.find((t) => t.id === id)?.name;

  return (
    <form onSubmit={handleSubmit(submit)} className="flex min-h-0 flex-1 flex-col">
      <SheetBody className="space-y-5 px-6 pb-6">
        {!entry && (
          <div className="space-y-1.5">
            <Label>Patient</Label>
            <Controller
              control={control}
              name="patientId"
              render={({ field }) => (
                <PatientCombobox
                  value={field.value || null}
                  onValueChange={(id) => {
                    // Picking the same patient again changes nothing.
                    if ((id ?? '') === field.value) return;
                    field.onChange(id ?? '');
                    // A type that came from the old patient's appointment goes with it.
                    if (getValues('appointmentId')) setValue('appointmentTypeId', null);
                    setValue('appointmentId', null);
                    autoPickedFor.current = null;
                  }}
                  hasError={!!formState.errors.patientId}
                />
              )}
            />
            {formState.errors.patientId && (
              <p className="text-xs text-destructive">{formState.errors.patientId.message}</p>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Appointment to move sooner</Label>
          <Controller
            control={control}
            name="appointmentId"
            render={({ field }) => (
              <Select
                value={field.value ?? NONE}
                onValueChange={(v) => {
                  const id = v === NONE ? null : v;
                  field.onChange(id);
                  // An appointment can give the EHR the location (ADR-292).
                  clearErrors('locationId');
                  const appt = appointments.find((a) => a.id === id);
                  if (appt) setValue('appointmentTypeId', appt.appointmentTypeId);
                }}
                disabled={!patientId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None — no appointment yet</SelectItem>
                  {appointments.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {formatDateTime(a.startTime)}
                      {a.provider ? ` · ${a.provider.firstName} ${a.provider.lastName}` : ''}
                    </SelectItem>
                  ))}
                  {entry?.appointment &&
                    !appointments.some((a) => a.id === entry.appointment?.id) && (
                      <SelectItem value={entry.appointment.id}>
                        {formatDateTime(entry.appointment.startTime)}
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-1.5">
          <Label>Appointment type</Label>
          <Controller
            control={control}
            name="appointmentTypeId"
            render={({ field }) =>
              appointmentId ? (
                <p className="text-sm text-muted-foreground">
                  {typeName(linked?.appointmentTypeId ?? field.value) ?? 'From the appointment'}{' '}
                  (from the appointment)
                </p>
              ) : (
                <Select
                  value={field.value ?? NONE}
                  onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not set</SelectItem>
                    {appointmentTypes.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <Controller
              control={control}
              name="providerId"
              render={({ field }) => (
                <EntityCombobox
                  config={providerPickerConfig}
                  loadStrategy="preload"
                  value={field.value}
                  onChange={(id) => field.onChange(id)}
                  placeholder="Any provider"
                  searchPlaceholder="Search providers…"
                />
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Controller
              control={control}
              name="locationId"
              render={({ field }) => (
                <EntityCombobox
                  config={locationPickerConfig}
                  loadStrategy="preload"
                  value={field.value}
                  onChange={(id) => {
                    field.onChange(id);
                    clearErrors('locationId');
                  }}
                  placeholder="Any location"
                  searchPlaceholder="Search locations…"
                />
              )}
            />
            {formState.errors.locationId && (
              <p className="text-xs text-destructive" role="alert">
                {formState.errors.locationId.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Days the patient can come</Label>
          <Controller
            control={control}
            name="days"
            render={({ field }) => (
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Days">
                {DAY_PICKER_ORDER.map((day) => {
                  const active = field.value.includes(day);
                  return (
                    <button
                      type="button"
                      key={day}
                      aria-pressed={active}
                      onClick={() =>
                        field.onChange(
                          active ? field.value.filter((d) => d !== day) : [...field.value, day]
                        )
                      }
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs transition-colors',
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-input text-muted-foreground hover:bg-accent'
                      )}
                    >
                      {DAY_SHORT[day]}
                    </button>
                  );
                })}
              </div>
            )}
          />
          <p className="text-xs text-muted-foreground">None selected means any day.</p>
        </div>

        <div className="space-y-1.5">
          <Label>Start time</Label>
          <div className="flex items-center gap-2">
            <TimeField control={control} name="earliestTime" label="Earliest start" />
            <span className="text-sm text-muted-foreground">up to</span>
            <TimeField control={control} name="latestTime" label="Start before" />
          </div>
          {formState.errors.latestTime ? (
            <p className="text-xs text-destructive">{formState.errors.latestTime.message}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Leave blank for any time. The appointment must start before the second time, so enter
              4:15 PM to include a 4:00 PM slot. The EHR keeps whole hours, so it gets the window
              widened to the hour.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Priority</Label>
          <Controller
            control={control}
            name="priority"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(v) => field.onChange(v as WaitlistPriority)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WAITLIST_PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {PRIORITY_LABEL[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="waitlist-notes">Notes for staff</Label>
          <Controller
            control={control}
            name="notes"
            render={({ field }) => <Textarea id="waitlist-notes" rows={3} {...field} />}
          />
        </div>
      </SheetBody>
      <SheetActions className="px-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <SaveButton type="submit" isPending={formState.isSubmitting}>
          {submitLabel}
        </SaveButton>
      </SheetActions>
    </form>
  );
}

/** A typed "HH:MM" start bound in 15-minute steps; blank = any. */
function TimeField({
  control,
  name,
  label,
}: {
  control: ReturnType<typeof useForm<WaitlistFormValues>>['control'];
  name: 'earliestTime' | 'latestTime';
  label: string;
}): React.ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Input
          type="time"
          step={900}
          aria-label={label}
          className="w-36"
          value={field.value ?? ''}
          onChange={(e) => field.onChange(e.target.value ? e.target.value.slice(0, 5) : null)}
        />
      )}
    />
  );
}
